import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { AccessPermissions, NotificationPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridTableContext, useNotificationContext } from 'core/states';
import { goTo } from 'core/utils';
import { useCustomerOrderActions } from 'orders/hooks';
import { oldOrderPath, orderPartsPath } from 'orders/routes/routePaths';
import { useQuoteActions } from 'quotes/hooks';
import { mapQuotePartsCreateOrder } from 'quotes/mappers';
import { ICreateOrderForm, ICustomerQuote } from 'quotes/types';

import { QuotePartCreateCustomerOrderForm } from './QuotePartCreateCustomerOrderForm';
import { quotePartCreateOrderSchema } from './schema';
import { getDefaultValues } from './utils';

interface IProps {
  isOpen?: boolean;
  onClose?: (closeSelection?: boolean) => void;
  quote: ICustomerQuote;
  isSelectionAction?: boolean;
}

export const QuotePartCreateCustomerOrder = ({
  isOpen = false,
  onClose,
  quote,
  isSelectionAction = false,
}: IProps) => {
  const { clearDrawerNotification, showErrorNotification, showSuccessNotification } =
    useNotificationContext();
  const { selectedRows } = useAgGridTableContext();

  const { t } = useTranslation();
  const { user } = useAuthUser();
  const navigate = useNavigate();

  const { handleCliJob } = useCliJobHandler();
  const { createFromQuote } = useCustomerOrderActions();
  const { createCustomerOrderFromQuote } = useQuoteActions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({
    user,
  });

  const requiredFields = isAlternateVersionEnabled
    ? AlternateVersionHelper.isDdpOrderIntegrationEnabled({ user })
    : user.companySettings?.coreOrderIntegrationEnabled;

  const hasNewOrderAccess = user.resources?.includes(AccessPermissions.ACCESS_NEW_ORDER);

  const onSubmit = () => async (data: ICreateOrderForm) => {
    const action = hasNewOrderAccess ? createCustomerOrderFromQuote : createFromQuote;

    handleCliJob(
      action,
      mapQuotePartsCreateOrder(
        quote.id,
        selectedRows,
        data,
        isSelectionAction,
        isAlternateVersionEnabled
      ),
      {
        cliJobInProgressMessage: t('messageOrderCreateInProgress'),
        onCliJobFinished: (cliJob: ICliJob) => {
          const result = cliJob.result;

          showSuccessNotification(
            { message: t('messageOrderCreateSuccess') },
            NotificationPosition.FORM
          );

          handleOnClose(undefined, true);

          if (hasNewOrderAccess) {
            navigate(orderPartsPath((result as Record<string, ICustomerQuote>).order.id));

            return;
          }

          goTo(oldOrderPath(result[0].id));
        },
        onCliJobStopped: (cliJob: ICliJob) => {
          showErrorNotification(
            {
              title: t('errorTitleOrderCreate'),
              message: cliJob.statusDescription || cliJob.status,
              disableAutoClose: true,
            },
            NotificationPosition.FORM
          );
        },
        notificationPosition: NotificationPosition.FORM,
      }
    );
  };

  const handleOnClose = (reset?: UseFormReset<ICreateOrderForm>, closeSelection = false) => {
    clearDrawerNotification();
    reset?.(getDefaultValues(quote, isAlternateVersionEnabled));
    onClose(closeSelection);
  };

  return (
    <FormProvider
      schema={quotePartCreateOrderSchema(requiredFields, isAlternateVersionEnabled)}
      defaultValues={getDefaultValues(quote, isAlternateVersionEnabled)}
      resetDefaultValues={!isOpen}
    >
      <QuotePartCreateCustomerOrderForm
        quote={quote}
        isOpen={isOpen}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        requiredFields={requiredFields}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        externalCustomerNumberPayload={{
          jobName: quote.jobName,
          projectId: quote.vendorProjectId,
          customerUserId: quote.customerContactUserId,
          alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
        }}
      />
    </FormProvider>
  );
};
